// ============================================================================
// USE CINEMATIC FEED - Polls backend for new cinematic cards and broadcasts them
// Runs in the main LiveSession window. Cursor stored in localStorage so a
// second screen refresh doesn't replay already-seen cards.
// ============================================================================

import { useEffect, useRef, useCallback } from 'react';
import { api } from '../services/api';
import type { CinematicCard } from '../types/second-screen.types';

const POLL_INTERVAL_MS = 5_000;

function cursorKey(sessionId: string) {
  return `cinematic-cursor-${sessionId}`;
}

export function useCinematicFeed(
  sessionId: string,
  isActive: boolean,
  sendCinematicCard: (card: CinematicCard) => void,
) {
  const cursorRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get<{ cards: CinematicCard[] }>(
        `/api/sessions/${sessionId}/cinematic-feed?since=${cursorRef.current}`
      );
      if (!data.cards?.length) return;

      for (const card of data.cards) {
        sendCinematicCard(card);
        if (card.timestamp > cursorRef.current) {
          cursorRef.current = card.timestamp;
        }
      }
      // Persist cursor so second screen refresh doesn't replay
      localStorage.setItem(cursorKey(sessionId), String(cursorRef.current));
    } catch {
      // Silently ignore poll failures — not critical
    }
  }, [sessionId, sendCinematicCard]);

  useEffect(() => {
    if (!isActive) return;

    // Initialise cursor: use stored value or now (so we only show cards from this point forward)
    const stored = localStorage.getItem(cursorKey(sessionId));
    cursorRef.current = stored ? parseInt(stored, 10) : Date.now();
    if (!stored) localStorage.setItem(cursorKey(sessionId), String(cursorRef.current));

    timerRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [sessionId, isActive, poll]);
}
